import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import type { StepDefinition } from "@/shared/components/common/PropertyStepperScreen";
import { routes, type SelectPropertyTypeLocationState } from "@/app/navigation/routes";
import { useAuth } from "@/app/providers/useAuth";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useAddress } from "@/shared/hooks/useAddress";
import { getLocalSupportingDocuments } from "@/features/protest/services/localSupportingDocuments.service";
import { useHomePageViewModal } from "@/features/home/viewModal/homePageViewModal";
import { propertyService } from "@/features/properties/model/propertyServices";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
type PropertyType = "RESIDENTIAL" | "COMMERCIAL";

type DocItem = {
  id: string;
  title: string;
  iframeSrc: string;
  kind: "preview" | "signed";
  isComplete?: boolean;
};

function getUiPropertyTypeFromApi(property: PropertyApiResponse | undefined): PropertyType | null {
  const raw = [property?.property_type, property?.property_raw_type].filter(Boolean).join(" ");
  if (!raw) return null;
  const normalized = raw.toLowerCase();
  if (normalized.includes("commercial")) return "COMMERCIAL";
  if (normalized.includes("residential")) return "RESIDENTIAL";
  return null;
}

function getPreviewUrl(docType: string): string | null {
  let baseUrl;
  switch (docType) {
    case "AOA":
      baseUrl = "https://drive.google.com/file/d/1zFMwpNw3G3GeYR2tD4-zAY7u-7shtfsh";
      break;
    case "DTR":
      baseUrl = "https://drive.google.com/file/d/1GMoYrtwSAoo4P77LGguYWDBNFE7BHWFj";
      break;
    // case "HOMESTEAD":
    //   baseUrl = "https://drive.google.com/file/d/1oIuAjP2cSGm1nugX0e9cBmx68E_JHy1Y";
    //   break;
    case "AGREEMENT":
      baseUrl = "https://drive.google.com/file/d/1aNnLRmXR0nUpCJ8_uNU8PKx3L42ajqDZ";
      break;
    case "NOTICE":
      baseUrl = "https://drive.google.com/file/d/1hV1Mwf_VeXA7JGNAkXf3mPeXAFq_U0dF";
      break;
    default:
      return null;
  }
  return `${baseUrl}/preview`;
}

type ApiHomesteadExemptionDoc = {
  document_url?: string | null;
  homestead_document_url?: string | null;
  exemption_document_url?: string | null;
  status?: string | null;
};

type ApiPropertyProtestDoc = {
  signed_aoa?: string | null;
  signed_dtr?: string | null;
  signed_dtr_document_url?: string | null;
  signed_andy_app_agreement?: boolean | null;
  signed_andy_app_agreement_document_url?: string | null;
  signed_notice_of_protest?: boolean | null;
  signed_notice_of_protest_document_url?: string | null;
};

export const useSelectPropertyTypePageViewModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const homeVm = useHomePageViewModal();
  const { selectedAddress } = useAddress();

  const selectedPropertyId = selectedAddress || "";
  const userId = user?.id != null ? String(user.id) : "";

  const [selected, setSelected] = useState<PropertyType | null>(null);
  const [activeDoc, setActiveDoc] = useState<DocItem | null>(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isWearAndTearStepperOpen, setIsWearAndTearStepperOpen] = useState(false);
  const [wearAndTearCurrentStep, setWearAndTearCurrentStep] = useState(1);
  const [isSupportingDocsModalOpen, setIsSupportingDocsModalOpen] = useState(false);

  const canFetchPropertyDetails = Boolean(selectedPropertyId && selectedPropertyId !== "__add__");
  const {
    data: propertyDetails,
    isLoading: isPropertyDetailsLoading,
    error: propertyDetailsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.PROPERTY_DETAILS, selectedPropertyId],
    queryFn: () => propertyService.getPropertyById(selectedPropertyId),
    enabled: canFetchPropertyDetails,
  });

  useEffect(() => {
    if (selected !== null) return;
    const inferred = getUiPropertyTypeFromApi(propertyDetails);
    if (!inferred) return;
    setSelected(inferred);
  }, [propertyDetails, selected]);

  // Home journey "Supporting Documents" lands on this route with intent in `location.state`;
  // open the same supporting-documents stepper used after protest is filed (no payment/sign flow changes).
  useEffect(() => {
    const navState = location.state as SelectPropertyTypeLocationState | null | undefined;
    if (!navState?.openProtestSupportingDocuments) return;
    if (!selected) return;

    setIsSignatureModalOpen(false);
    setIsSupportingDocsModalOpen(false);
    setWearAndTearCurrentStep(1);
    setIsWearAndTearStepperOpen(true);
    navigate(routes.selectPropertyType, { replace: true, state: {} });
  }, [location.key, location.state, navigate, selected]);

  const docs = useMemo<DocItem[]>(() => {
    const all: DocItem[] = [
      { id: "aoa", title: "AOA Form", iframeSrc: getPreviewUrl("AOA") || "", kind: "preview" },
      { id: "dtr", title: "DTR Form", iframeSrc: getPreviewUrl("DTR") || "", kind: "preview" },
      {
        id: "pba",
        title: "Performance Based Agreement",
        iframeSrc: getPreviewUrl("AGREEMENT") || "",
        kind: "preview",
      },
      {
        id: "nop",
        title: "Notice of Protest",
        iframeSrc: getPreviewUrl("NOTICE") || "",
        kind: "preview",
      },
      // { id: "hs65", title: "Homestead/Over 65 Exemption", iframeSrc: getPreviewUrl("HOMESTEAD") || "", kind: "preview" },
    ];
    if (selected === "COMMERCIAL") {
      return all.filter((d) => d.id !== "nop" && d.id !== "hs65");
    }
    return all;
  }, [selected]);

  const signedDocs = useMemo<DocItem[]>(() => {
    const homestead = (propertyDetails?.documents?.homestead_exemption_documents ??
      []) as ApiHomesteadExemptionDoc[];
    const protest = (propertyDetails?.documents?.property_protest_documents ??
      []) as ApiPropertyProtestDoc[];

    const items: DocItem[] = [];

    const homesteadDoc = homestead.at(0);
    const homesteadUrl =
      homesteadDoc?.homestead_document_url ||
      homesteadDoc?.exemption_document_url ||
      homesteadDoc?.document_url ||
      null;
    const protestDoc = protest.at(0);
    const protestCandidates: Array<{
      id: string;
      title: string;
      url?: string | null;
      complete?: boolean;
    }> = [
      {
        id: "signed_aoa",
        title: "AOA Form",
        url: protestDoc?.signed_aoa,
        complete: Boolean(protestDoc?.signed_aoa),
      },
      {
        id: "signed_dtr",
        title: "DTR Form",
        url: protestDoc?.signed_dtr_document_url || protestDoc?.signed_dtr,
        complete: Boolean(protestDoc?.signed_dtr_document_url || protestDoc?.signed_dtr),
      },
      {
        id: "signed_agreement",
        title: "Performance Based Agreement",
        url: protestDoc?.signed_andy_app_agreement_document_url || null,
        complete: Boolean(
          protestDoc?.signed_andy_app_agreement_document_url ||
          protestDoc?.signed_andy_app_agreement
        ),
      },
      {
        id: "signed_notice",
        title: "Notice of Protest",
        url: protestDoc?.signed_notice_of_protest_document_url || null,
        complete: Boolean(
          protestDoc?.signed_notice_of_protest_document_url || protestDoc?.signed_notice_of_protest
        ),
      },
    ];

    const hasAnyProtestSigned = protestCandidates.some((c) => Boolean(c.complete));
    const shouldShowHomesteadRow = Boolean(homesteadDoc) || hasAnyProtestSigned;
    if (shouldShowHomesteadRow) {
      const isComplete =
        Boolean(homesteadUrl) && (homesteadDoc?.status ?? "").toLowerCase() === "completed";
      items.push({
        id: "signed_homestead_exemption",
        title: "Homestead / Over 65 Exemption",
        iframeSrc: homesteadUrl || "",
        kind: "signed",
        isComplete,
      });
    }

    for (const c of protestCandidates) {
      if (!c.complete) continue;
      if (!c.url) continue;
      items.push({
        id: c.id,
        title: c.title,
        iframeSrc: c.url,
        kind: "signed",
        isComplete: true,
      });
    }

    return items;
  }, [
    propertyDetails?.documents?.homestead_exemption_documents,
    propertyDetails?.documents?.property_protest_documents,
  ]);

  const hasSignedDocs = signedDocs.length > 0;

  const residentialSteps = useMemo<StepDefinition[]>(
    () => [
      { id: "intro", label: "Introduction" },
      { id: "wear", label: "Wear and Tear", supportingDocumentType: "PROPERTY_WEAR_AND_TEAR" },
      {
        id: "neighborhood",
        label: "Neighborhood Wear",
        supportingDocumentType: "NEIGHBORHOOD_CONDITION",
      },
      { id: "sympathy", label: "Sympathy Savings", supportingDocumentType: "SYMPATHY_SAVINGS" },
      { id: "review", label: "Review" },
    ],
    []
  );

  const commercialSteps = useMemo<StepDefinition[]>(
    () => [
      { id: "intro", label: "Introduction" },
      { id: "pictures", label: "Pictures", supportingDocumentType: "PROPERTY_PICTURES" },
      { id: "rent-roll", label: "Rent Roll", supportingDocumentType: "RENT_ROLL" },
      {
        id: "income-expense",
        label: "Income Expense",
        supportingDocumentType: "INCOME_EXPENSE_STATEMENT",
      },
      { id: "future", label: "Future Expenditures", supportingDocumentType: "FUTURE_EXPENDITURES" },
      { id: "appraisal", label: "Appraisal", supportingDocumentType: "APPRAISAL" },
      { id: "review", label: "Review" },
    ],
    []
  );

  const [localSupportingDocuments, setLocalSupportingDocuments] = useState(() =>
    getLocalSupportingDocuments(selectedPropertyId)
  );

  const refreshLocalSupportingDocuments = useCallback(() => {
    setLocalSupportingDocuments(getLocalSupportingDocuments(selectedPropertyId));
  }, [selectedPropertyId]);

  useEffect(() => {
    refreshLocalSupportingDocuments();
  }, [refreshLocalSupportingDocuments]);

  const canView = selected !== null;
  const iframeSrc = activeDoc ? activeDoc.iframeSrc : "";
  const selectedTypeDisplay = selected ? selected.charAt(0).toUpperCase() + selected.slice(1) : "";

  const isResidential = selected === "RESIDENTIAL";
  const steps = isResidential ? residentialSteps : commercialSteps;
  const currentStepDefinition = steps[wearAndTearCurrentStep - 1];
  const currentStepTitle = currentStepDefinition?.label ?? "Step";
  const maxSteps = isResidential ? 5 : 7;

  const lastStepHasAnySupportingDocuments = useMemo(() => {
    const flowTypes = new Set(
      steps
        .map((s) => s.supportingDocumentType)
        .filter((t): t is NonNullable<typeof t> => Boolean(t))
    );
    return localSupportingDocuments.some((d) => flowTypes.has(d.documentType));
  }, [localSupportingDocuments, steps]);

  useEffect(() => {
    if (
      selected === "COMMERCIAL" &&
      activeDoc &&
      (activeDoc.id === "nop" || activeDoc.id === "hs65")
    ) {
      setActiveDoc(null);
    }
  }, [selected, activeDoc]);

  useEffect(() => {
    if (!activeDoc) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveDoc(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeDoc]);

  useEffect(() => {
    if (!isWearAndTearStepperOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsWearAndTearStepperOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isWearAndTearStepperOpen]);

  const isSelected = useCallback((type: PropertyType) => selected === type, [selected]);

  const onSelectPropertyType = useCallback((type: PropertyType) => {
    setSelected(type);
  }, []);

  const onOpenDoc = useCallback(
    (doc: DocItem) => {
      if (!selected) return;
      setActiveDoc(doc);
    },
    [selected]
  );

  const onCloseDoc = useCallback(() => {
    setActiveDoc(null);
  }, []);

  const onSignature = useCallback(() => {
    if (!canView) return;
    const hasProtestFiled =
      homeVm.protestStatus || propertyDetails?.protest_status === "filed" || hasSignedDocs;
    if (hasProtestFiled) {
      setWearAndTearCurrentStep(1);
      setIsWearAndTearStepperOpen(true);
      setIsSignatureModalOpen(false);
      return;
    }
    setIsSignatureModalOpen(true);
  }, [canView, homeVm.protestStatus, propertyDetails?.protest_status, hasSignedDocs]);

  const onCloseSignatureModal = useCallback(() => {
    setIsSignatureModalOpen(false);
  }, []);

  const onSignatureModalSubmit = useCallback(() => {
    setWearAndTearCurrentStep(1);
    setIsWearAndTearStepperOpen(true);
  }, []);

  const onCloseStepper = useCallback(() => {
    setIsWearAndTearStepperOpen(false);
  }, []);

  const onStepperBack = useCallback(() => {
    refreshLocalSupportingDocuments();
    if (wearAndTearCurrentStep <= 1) {
      setIsWearAndTearStepperOpen(false);
      setWearAndTearCurrentStep(1);
      navigate(routes.homepage);
      return;
    }
    setWearAndTearCurrentStep((step) => step - 1);
  }, [refreshLocalSupportingDocuments, navigate, wearAndTearCurrentStep]);

  const onStepperNext = useCallback(() => {
    refreshLocalSupportingDocuments();
    setWearAndTearCurrentStep((step) => {
      if (step >= maxSteps) return step;
      return step + 1;
    });
  }, [maxSteps, refreshLocalSupportingDocuments]);

  const onStepperSkip = useCallback(() => {
    refreshLocalSupportingDocuments();
    setWearAndTearCurrentStep((step) => {
      if (step >= maxSteps) return step;
      return step + 1;
    });
  }, [maxSteps, refreshLocalSupportingDocuments]);

  const onStepperSubmit = useCallback(() => {
    setIsWearAndTearStepperOpen(false);
    setIsSupportingDocsModalOpen(true);
  }, []);

  const onCloseSupportingDocsModal = useCallback(() => {
    setIsSupportingDocsModalOpen(false);
  }, []);

  return {
    userId,
    selectedPropertyId,
    propertyDetails,
    isPropertyDetailsLoading,
    propertyDetailsError,
    selected,
    docs,
    signedDocs,
    hasSignedDocs,
    canView,
    isSelected,
    onSelectPropertyType,
    onSignature,
    activeDoc,
    onOpenDoc,
    onCloseDoc,
    iframeSrc,
    selectedTypeDisplay,
    isSignatureModalOpen,
    onCloseSignatureModal,
    onSignatureModalSubmit,
    isWearAndTearStepperOpen,
    onCloseStepper,
    wearAndTearCurrentStep,
    isResidential,
    currentStepTitle,
    onStepperBack,
    onStepperNext,
    onStepperSkip,
    onStepperSubmit,
    residentialSteps,
    commercialSteps,
    localSupportingDocuments,
    lastStepHasAnySupportingDocuments,
    isSupportingDocsModalOpen,
    onCloseSupportingDocsModal,
  };
};
